import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Typography, Grid, Card, Collapse, Space, Tag, theme } from "antd";
import ResponsiveModal from '../../Shared/ResponsiveModal/ResponsiveModal';
import { useTranslation } from 'react-i18next';
import useMobileFullscreenModalBackHandler from "../../../Hooks/useMobileFullscreenModalBackHandler";

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const formatKey = (key) => {
    return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeDateString = (value) => {
    if (typeof value !== 'string') return null;

    const match = value.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(\.(\d+))?(Z|[+-]\d{2}:\d{2})$/);
    if (!match) return null;

    const [, dateTime, , fractional = '', timezone] = match;
    const milliseconds = fractional ? `.${fractional.slice(0, 3).padEnd(3, '0')}` : '';
    const normalized = `${dateTime}${milliseconds}${timezone}`;

    return Number.isNaN(Date.parse(normalized)) ? null : normalized;
};

// Format value based on its type
const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';

    const normalizedDate = normalizeDateString(value);
    if (normalizedDate) {
        try {
            return new Date(normalizedDate).toLocaleString();
        } catch (e) {
            return String(value);
        }
    }

    return String(value);
};

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const formatDetailValue = (value) => {
    if (React.isValidElement(value)) return value;
    if (value !== null && typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }
    return formatValue(value);
};

const getTransactionTypeColor = (type) => {
    switch (type) {
        case 'payment':
            return 'green';
        case 'refund':
            return 'red';
        case 'admin':
            return 'purple';
        case 'system':
            return 'cyan';
        case 'message':
            return 'blue';
        default:
            return 'default';
    }
};

const getStatusColor = (status) => {
    switch (status) {
        case 'completed':
            return 'success';
        case 'pending':
            return 'warning';
        case 'cancelled':
        case 'failed':
            return 'error';
        case 'disabled':
            return 'default';
        default:
            return 'default';
    }
};

const TransactionDetailsModal = ({ visible, onCancel, transactionDetails }) => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { token } = theme.useToken();
    const isMobile = screens.xs;

    // Close fullscreen modal with the mobile system back button
    useMobileFullscreenModalBackHandler(visible && isMobile, onCancel);

    const translate = (key, fallback) => t(key, { defaultValue: fallback || formatKey(key) });
    const formatEnum = (value) => value ? t(value, { defaultValue: formatKey(value) }) : formatValue(value);
    const formatBoolean = (value) => value ? t('yes', { defaultValue: 'Yes' }) : t('no', { defaultValue: 'No' });

    const sectionCardStyles = {
        body: {
            padding: isMobile ? 12 : 16,
            background: token.colorBgContainer,
            color: token.colorText
        }
    };

    const detailGridStyle = {
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
        columnGap: 16,
        rowGap: 12
    };

    const detailLabelStyle = {
        display: 'block',
        color: token.colorTextSecondary,
        fontSize: 12,
        marginBottom: 2
    };

    const textBlockStyle = {
        margin: 0,
        padding: 12,
        background: token.colorFillAlter,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 6,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word'
    };

    const renderDetailItem = (label, value, key = label) => (
        <div key={key}>
            <Text style={detailLabelStyle}>{label}</Text>
            <Text
                style={{
                    color: token.colorText,
                    wordBreak: 'break-word',
                    whiteSpace: value !== null && typeof value === 'object' && !React.isValidElement(value) ? 'pre-wrap' : 'normal'
                }}
            >
                {formatDetailValue(value)}
            </Text>
        </div>
    );

    const renderPerson = (title, person, fallbackId) => {
        if (!person && !fallbackId) return null;

        return (
            <Card bordered size={isMobile ? 'small' : 'default'} styles={sectionCardStyles}>
                <Title level={5} style={{ marginTop: 0 }}>{title}</Title>
                <div style={detailGridStyle}>
                    {renderDetailItem(t('full_name'), formatValue(person?.full_name))}
                    {renderDetailItem(t('id'), formatValue(person?.id || person?.user_id || fallbackId))}
                </div>
            </Card>
        );
    };

    const renderStatusRow = (key, isActive, date) => (
        <div
            key={key}
            style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '140px 92px minmax(0, 1fr)',
                gap: isMobile ? 4 : 12,
                alignItems: 'center',
                padding: '8px 0',
                borderBottom: `1px solid ${token.colorBorderSecondary}`
            }}
        >
            <Text>{translate(key)}</Text>
            <Tag color={isActive ? 'success' : 'default'} style={{ width: 'fit-content' }}>
                {formatBoolean(!!isActive)}
            </Tag>
            <Text type="secondary">{hasValue(date) ? formatValue(date) : t('not_available', { defaultValue: 'N/A' })}</Text>
        </div>
    );

    const messageStatus = transactionDetails?.message?.status || {};

    const transactionRows = useMemo(() => {
        if (!transactionDetails) return [];

        return [
            [t('transaction_type'), <Tag color={getTransactionTypeColor(transactionDetails.transaction_type)}>{formatEnum(transactionDetails.transaction_type)}</Tag>, 'transaction_type'],
            [t('transaction_status'), <Tag color={getStatusColor(transactionDetails.transaction_status)}>{formatEnum(transactionDetails.transaction_status)}</Tag>, 'transaction_status'],
            [t('coins_amount'), formatValue(transactionDetails.coins_amount), 'coins_amount'],
            hasValue(transactionDetails.paid_amount) && [translate('paid_amount'), formatValue(transactionDetails.paid_amount), 'paid_amount'],
            [t('created_at'), formatValue(transactionDetails.created_at), 'created_at'],
            [t('updated_at'), formatValue(transactionDetails.updated_at), 'updated_at'],
            [translate('is_disabled'), <Tag color={transactionDetails.is_disabled ? 'default' : 'success'}>{transactionDetails.is_disabled ? t('disabled') : t('enabled', { defaultValue: 'Enabled' })}</Tag>, 'is_disabled'],
            hasValue(transactionDetails.cancellation_reason) && [t('cancellation_reason'), formatValue(transactionDetails.cancellation_reason), 'cancellation_reason'],
            (transactionDetails.is_disabled || hasValue(transactionDetails.disabling_reason)) && [t('disabling_reason'), formatValue(transactionDetails.disabling_reason), 'disabling_reason'],
            hasValue(transactionDetails.disabled_at) && [translate('disabled_at'), formatValue(transactionDetails.disabled_at), 'disabled_at'],
        ].filter(Boolean);
    }, [transactionDetails, t]); // eslint-disable-line react-hooks/exhaustive-deps

    const paymentRows = useMemo(() => {
        const payment = transactionDetails?.payment;
        if (!payment) return [];

        return [
            [t('id'), payment.id, 'payment.id'],
            [translate('payment_type'), formatEnum(payment.payment_type), 'payment.payment_type'],
            [translate('payment_status'), formatEnum(payment.payment_status), 'payment.payment_status'],
            [translate('sub_total'), payment.sub_total, 'payment.sub_total'],
            [translate('total'), payment.total, 'payment.total'],
            [translate('currency'), payment.currency, 'payment.currency'],
            [translate('tax_amount'), payment.tax_amount, 'payment.tax_amount'],
            [translate('discount_amount'), payment.discount_amount, 'payment.discount_amount'],
            [translate('promotion_code'), payment.promotion_code, 'payment.promotion_code'],
            hasValue(payment.paid_at) && [translate('paid_at'), formatValue(payment.paid_at), 'payment.paid_at'],
            [translate('is_refunded'), formatBoolean(!!payment.is_refunded), 'payment.is_refunded'],
            [translate('is_disputed'), formatBoolean(!!payment.is_disputed), 'payment.is_disputed'],
            [translate('has_error'), formatBoolean(!!payment.has_error), 'payment.has_error'],
        ].filter(row => row && hasValue(row[1]));
    }, [transactionDetails, t]); // eslint-disable-line react-hooks/exhaustive-deps

    const relatedRows = useMemo(() => {
        if (!transactionDetails) return [];

        return [
            hasValue(transactionDetails.event_type) && [translate('event_type'), formatEnum(transactionDetails.event_type), 'event_type'],
            hasValue(transactionDetails.beautify_type) && [translate('beautify_type'), formatEnum(transactionDetails.beautify_type), 'beautify_type'],
            transactionDetails.agent && [translate('agent'), `${formatValue(transactionDetails.agent.name)} (${formatValue(transactionDetails.agent.id)})`, 'agent'],
            transactionDetails.knowledge_base && [translate('knowledge_base'), `${formatValue(transactionDetails.knowledge_base.name)} (${formatValue(transactionDetails.knowledge_base.id)})`, 'knowledge_base'],
            transactionDetails.conversation && [translate('conversation'), `${formatValue(transactionDetails.conversation.id)} · ${formatEnum(transactionDetails.conversation.status)}`, 'conversation'],
        ].filter(Boolean);
    }, [transactionDetails, t]); // eslint-disable-line react-hooks/exhaustive-deps

    const advancedRows = useMemo(() => {
        if (!transactionDetails) return [];

        const handledTopLevelKeys = new Set([
            'id',
            'user_id',
            'user',
            'transaction_type',
            'transaction_status',
            'coins_amount',
            'description',
            'cancellation_reason',
            'is_disabled',
            'disabling_reason',
            'disabled_by_id',
            'disabled_by',
            'disabled_at',
            'created_at',
            'updated_at',
            'paid_amount',
            'admin',
            'message',
            'payment',
            'event_type',
            'beautify_type',
            'agent',
            'knowledge_base',
            'conversation'
        ]);

        const extraRows = Object.entries(transactionDetails)
            .filter(([key, value]) => !handledTopLevelKeys.has(key) && hasValue(value))
            .map(([key, value]) => [formatKey(key), value, `extra.${key}`]);

        return [
            [t('id'), transactionDetails.id, 'id'],
            [translate('user_id'), transactionDetails.user_id, 'user_id'],
            transactionDetails.user && [translate('user_id'), transactionDetails.user.id || transactionDetails.user.user_id, 'user.id'],
            hasValue(transactionDetails.disabled_by_id) && [translate('disabled_by_id'), transactionDetails.disabled_by_id, 'disabled_by_id'],
            transactionDetails.admin && [translate('admin_id'), transactionDetails.admin.id, 'admin.id'],
            transactionDetails.message && [translate('message_id'), transactionDetails.message.id, 'message.id'],
            transactionDetails.message && [t('sender_type'), formatEnum(transactionDetails.message.sender_type), 'message.sender_type'],
            hasValue(messageStatus.sent_at) && [t('sent_at'), formatValue(messageStatus.sent_at), 'sent_at'],
            hasValue(messageStatus.delivered_at) && [t('delivered_at'), formatValue(messageStatus.delivered_at), 'delivered_at'],
            hasValue(messageStatus.read_at) && [t('read_at'), formatValue(messageStatus.read_at), 'read_at'],
            hasValue(messageStatus.failed_at) && [translate('failed_at'), formatValue(messageStatus.failed_at), 'failed_at'],
            hasValue(messageStatus.edited_at) && [translate('edited_at'), formatValue(messageStatus.edited_at), 'edited_at'],
            transactionDetails.payment?.refund && [translate('refund'), transactionDetails.payment.refund, 'payment.refund'],
            transactionDetails.payment?.dispute && [translate('dispute'), transactionDetails.payment.dispute, 'payment.dispute'],
            transactionDetails.payment?.failure && [translate('failure'), transactionDetails.payment.failure, 'payment.failure'],
            ...extraRows,
        ].filter(row => row && hasValue(row[1]));
    }, [transactionDetails, messageStatus, t]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!transactionDetails) {
        return (
            <ResponsiveModal
                title={<Title level={4}>{t('transaction_details')}</Title>}
                visible={visible}
                onCancel={onCancel}
                footer={null}
                desktopWidth={720}
            >
                <Typography.Text>{t('no_transaction_details_available')}</Typography.Text>
            </ResponsiveModal>
        );
    }

    return (
        <ResponsiveModal
            title={<Title level={isMobile ? 5 : 4} style={{ margin: 0 }}>{t('transaction_details')}</Title>}
            visible={visible}
            onCancel={onCancel}
            footer={null}
            desktopWidth={720}
        >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Card bordered size={isMobile ? 'small' : 'default'} styles={sectionCardStyles}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Text strong>{t('summary')}</Text>
                        <Space size={8} wrap>
                            <Title
                                level={isMobile ? 4 : 3}
                                style={{
                                    margin: 0,
                                    color: Number(transactionDetails.coins_amount) >= 0 ? token.colorSuccess : token.colorError
                                }}
                            >
                                {formatValue(transactionDetails.coins_amount)} {t('coins', { defaultValue: 'coins' })}
                            </Title>
                            <Tag color={getTransactionTypeColor(transactionDetails.transaction_type)}>{formatEnum(transactionDetails.transaction_type)}</Tag>
                            <Tag color={getStatusColor(transactionDetails.transaction_status)}>{formatEnum(transactionDetails.transaction_status)}</Tag>
                            {transactionDetails.is_disabled && <Tag color="default">{t('disabled')}</Tag>}
                        </Space>
                        <Text>{formatValue(transactionDetails.description)}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('id')}: {formatValue(transactionDetails.id)} · {t('created_at')}: {formatValue(transactionDetails.created_at)}
                        </Text>
                    </Space>
                </Card>

                <Card bordered size={isMobile ? 'small' : 'default'} styles={sectionCardStyles}>
                    <Title level={5} style={{ marginTop: 0 }}>{t('transaction_details')}</Title>
                    <div style={detailGridStyle}>
                        {transactionRows.map(([label, value, key]) => renderDetailItem(label, value, key))}
                    </div>
                </Card>

                {renderPerson(t('user_details'), transactionDetails.user, transactionDetails.user_id)}
                {renderPerson(t('admin'), transactionDetails.admin)}
                {renderPerson(translate('disabled_by'), transactionDetails.disabled_by, transactionDetails.disabled_by_id)}

                {paymentRows.length > 0 && (
                    <Card bordered size={isMobile ? 'small' : 'default'} styles={sectionCardStyles}>
                        <Title level={5} style={{ marginTop: 0 }}>{translate('payment_details')}</Title>
                        <div style={detailGridStyle}>
                            {paymentRows.map(([label, value, key]) => renderDetailItem(label, value, key))}
                        </div>
                    </Card>
                )}

                {relatedRows.length > 0 && (
                    <Card bordered size={isMobile ? 'small' : 'default'} styles={sectionCardStyles}>
                        <Title level={5} style={{ marginTop: 0 }}>{translate('related_details')}</Title>
                        <div style={detailGridStyle}>
                            {relatedRows.map(([label, value, key]) => renderDetailItem(label, value, key))}
                        </div>
                    </Card>
                )}

                {transactionDetails.message && (
                    <Card bordered size={isMobile ? 'small' : 'default'} styles={sectionCardStyles}>
                        <Title level={5} style={{ marginTop: 0 }}>{t('message_details')}</Title>
                        <div style={detailGridStyle}>
                            {renderDetailItem(t('id'), formatValue(transactionDetails.message.id), 'message_id')}
                            {renderDetailItem(t('sender_type'), <Tag color="blue">{formatEnum(transactionDetails.message.sender_type)}</Tag>, 'sender_type')}
                        </div>
                        <Paragraph style={{ ...detailLabelStyle, marginTop: 14 }}>{t('content', { defaultValue: 'Content' })}</Paragraph>
                        {hasValue(transactionDetails.message.content) ? (
                            <Paragraph style={textBlockStyle}>{transactionDetails.message.content}</Paragraph>
                        ) : (
                            <Text type="secondary">{t('no_content', { defaultValue: 'No content' })}</Text>
                        )}
                    </Card>
                )}

                {transactionDetails.message?.status && (
                    <Card bordered size={isMobile ? 'small' : 'default'} styles={sectionCardStyles}>
                        <Title level={5} style={{ marginTop: 0 }}>{t('status')}</Title>
                        {[
                            ['sent', messageStatus.sent, messageStatus.sent_at],
                            ['delivered', messageStatus.delivered, messageStatus.delivered_at],
                            ['read', messageStatus.read, messageStatus.read_at],
                            ['failed', messageStatus.failed, messageStatus.failed_at],
                            ['edited', messageStatus.edited, messageStatus.edited_at],
                        ].map(([key, isActive, date]) => renderStatusRow(key, isActive, date))}
                    </Card>
                )}

                {advancedRows.length > 0 && (
                    <Collapse ghost>
                        <Collapse.Panel header={t('more_details')} key="more">
                            <div style={detailGridStyle}>
                                {advancedRows.map(([label, value, key]) => renderDetailItem(label, value, key))}
                            </div>
                        </Collapse.Panel>
                    </Collapse>
                )}
            </Space>
        </ResponsiveModal>
    );
};

TransactionDetailsModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    transactionDetails: PropTypes.shape({
        user: PropTypes.shape({
            user_id: PropTypes.string,
            full_name: PropTypes.string,
        }),
        message: PropTypes.object,
        admin: PropTypes.object,
    }),
};

export default TransactionDetailsModal;
